import Phaser from 'phaser';
import { GAME_FONT_FAMILY, GOLD_TEXT_COLOR } from '@shared/constants';
import type {
  ExchangeOpenedMessage,
  ExchangeBookMessage,
  ExchangeClaimsMessage,
  InventorySlotData,
} from '@shared/types/messages';
import type {
  ExchangeItemEntry,
  OrderBookLevel,
  MarketOrderView,
  MarketClaimView,
  MarketTradeView,
  PriceCandle,
  MarketWatchView,
  MarketAlertView,
  WatchDirection,
} from '@shared/types/market';
import { MarketSide } from '@shared/types/market';
import { createUIIcon } from '../../sprites/IconAtlases';

/**
 * ExchangePanel — UI del EXCHANGE (mercado de fungibles, modelo OSRS GE) con
 * estética de producto financiero (Binance/Steam Market): tarjetas con ícono,
 * libro de órdenes con BARRAS DE PROFUNDIDAD (verde bids / rojo asks), último
 * precio con ▲/▼, trades recientes coloreados, y formulario de orden.
 *
 * Ver docs/ECONOMY_EXCHANGE_MARKETPLACE.md.
 */

export interface ExchangePanelCallbacks {
  onViewItem: (itemId: string) => void;
  onCreateOrder: (itemId: string, side: MarketSide, price: number, quantity: number) => void;
  onCancelOrder: (orderId: string) => void;
  onClaimAll: () => void;
  onAddWatch: (itemId: string, direction: WatchDirection, targetPrice: number) => void;
  onRemoveWatch: (watchId: string) => void;
  onAlertsSeen: () => void;
  /** Cambiar a la pestaña Bazar (Marketplace). */
  onSwitchToMarketplace?: () => void;
  onClose?: () => void;
  onMove?: (x: number, y: number) => void;
}

export interface ExchangePanelOptions {
  initialX: number;
  initialY: number;
}

const WIDTH = 800;
const HEIGHT = 510;
const HEADER_H = 38;
const DEPTH_BG = 1200;

const COL_CATALOG_W = 232;
const COL_BOOK_W = 300;

// Paleta
const C_PANEL = 0x16130b;
const C_HEADER = 0x261d0e;
const C_ACCENT = 0xc79a3e;
const C_CARD = 0x1d1810;
const C_CARD_SEL = 0x3a2e14;
const C_GREEN = 0x46c46a;
const C_RED = 0xe06a6a;
const C_GREEN_BAR = 0x1e4a2c;
const C_RED_BAR = 0x4a2222;
const T_DIM = '#8a7e54';
const T_LABEL = '#9a8a5a';
const T_BODY = '#e8dcb0';

function font<T extends Phaser.Types.GameObjects.Text.TextStyle>(style?: T): T {
  return { fontFamily: GAME_FONT_FAMILY, resolution: 2, ...style } as T;
}

export class ExchangePanel {
  private scene: Phaser.Scene;
  private callbacks: ExchangePanelCallbacks;
  private container: Phaser.GameObjects.Container;
  private isOpen = false;
  private npcId: string | null = null;

  private x: number;
  private y: number;

  private catalog: ExchangeItemEntry[] = [];
  private selectedItemId: string | null = null;
  private book: { bids: OrderBookLevel[]; asks: OrderBookLevel[]; lastPrice: number | null } = {
    bids: [],
    asks: [],
    lastPrice: null,
  };
  private myOrders: MarketOrderView[] = [];
  private recentTrades: MarketTradeView[] = [];
  private history: PriceCandle[] = [];
  private historyDay: PriceCandle[] = [];
  private chartBucket: '1h' | '1d' = '1h';
  private claims: MarketClaimView[] = [];
  private gold = 0;
  private ownedByItem: Map<string, number> = new Map();

  private side: MarketSide = MarketSide.BUY;
  private price = 1;
  private quantity = 1;

  // Comerciante (watchlist + alertas)
  private watches: MarketWatchView[] = [];
  private alertUnseen = 0;
  private watchDir: WatchDirection = 'below';
  private watchPrice = 1;

  private dragging = false;
  private dragOffsetX = 0;
  private dragOffsetY = 0;

  private dynamicObjects: Phaser.GameObjects.GameObject[] = [];
  private goldText!: Phaser.GameObjects.Text;

  constructor(scene: Phaser.Scene, callbacks: ExchangePanelCallbacks, options: ExchangePanelOptions) {
    this.scene = scene;
    this.callbacks = callbacks;
    this.x = options.initialX;
    this.y = options.initialY;
    this.container = scene.add.container(this.x, this.y).setDepth(DEPTH_BG).setVisible(false);
    this.container.setScrollFactor(0);
    this.buildChrome();
  }

  // ===========================================================================
  // API pública
  // ===========================================================================

  public open(message: ExchangeOpenedMessage, gold: number, inventory?: InventorySlotData[]): void {
    this.npcId = message.npcId;
    this.catalog = message.items;
    this.gold = gold;
    this.claims = message.claims;
    if (inventory) this.setInventory(inventory);
    if (!this.selectedItemId && this.catalog.length > 0) {
      this.selectedItemId = this.catalog[0].itemId;
    }
    this.isOpen = true;
    this.container.setVisible(true);
    this.render();
    if (this.selectedItemId) this.callbacks.onViewItem(this.selectedItemId);
  }

  public isOpenPanel(): boolean {
    return this.isOpen;
  }

  public getCurrentNpcId(): string | null {
    return this.npcId;
  }

  public close(silent = false): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.dragging = false;
    this.container.setVisible(false);
    if (!silent) this.callbacks.onClose?.();
  }

  public setGold(gold: number): void {
    this.gold = gold;
    if (this.goldText) this.goldText.setText(formatGold(gold));
  }

  public setInventory(inventory: InventorySlotData[]): void {
    this.ownedByItem.clear();
    for (const slot of inventory) {
      this.ownedByItem.set(slot.itemId, (this.ownedByItem.get(slot.itemId) ?? 0) + slot.quantity);
    }
    if (this.isOpen) this.render();
  }

  public applyBook(message: ExchangeBookMessage): void {
    if (message.itemId !== this.selectedItemId) return;
    this.book = { bids: message.bids, asks: message.asks, lastPrice: message.lastPrice };
    this.myOrders = message.myOrders;
    this.recentTrades = message.recentTrades;
    this.history = message.history ?? [];
    this.historyDay = message.historyDay ?? [];
    this.price = this.defaultPriceForSide(this.side);
    if (this.isOpen) this.render();
  }

  public applyClaims(message: ExchangeClaimsMessage): void {
    this.claims = message.claims;
    if (this.isOpen) this.render();
  }

  public applyWatches(watches: MarketWatchView[]): void {
    this.watches = watches;
    if (this.isOpen) this.render();
  }

  public applyAlerts(alerts: MarketAlertView[]): void {
    this.alertUnseen = alerts.filter((a) => !a.seen).length;
    if (this.isOpen) this.render();
  }

  public hasClaims(): boolean {
    return this.claims.length > 0;
  }

  public isDraggingPanel(): boolean {
    return this.dragging;
  }

  public isPointerOver(pointer: Phaser.Input.Pointer): boolean {
    if (!this.isOpen) return false;
    return (
      pointer.x >= this.x &&
      pointer.x <= this.x + WIDTH &&
      pointer.y >= this.y &&
      pointer.y <= this.y + HEIGHT
    );
  }

  public handlePointerMove(pointer: Phaser.Input.Pointer): void {
    if (!this.dragging) return;
    this.x = pointer.x - this.dragOffsetX;
    this.y = pointer.y - this.dragOffsetY;
    this.container.setPosition(this.x, this.y);
  }

  public handlePointerUp(_pointer: Phaser.Input.Pointer): void {
    if (this.dragging) {
      this.dragging = false;
      this.callbacks.onMove?.(this.x, this.y);
    }
  }

  public destroy(): void {
    this.container.destroy(true);
  }

  // ===========================================================================
  // Chrome estático
  // ===========================================================================

  private buildChrome(): void {
    const bg = this.scene.add
      .rectangle(0, 0, WIDTH, HEIGHT, C_PANEL, 0.98)
      .setOrigin(0, 0)
      .setStrokeStyle(2, C_ACCENT, 1);
    this.container.add(bg);

    // Separadores de columna sutiles.
    const sep1 = this.scene.add.rectangle(COL_CATALOG_W, HEADER_H, 1, HEIGHT - HEADER_H, C_ACCENT, 0.25).setOrigin(0, 0);
    const sep2 = this.scene.add
      .rectangle(COL_CATALOG_W + COL_BOOK_W, HEADER_H, 1, HEIGHT - HEADER_H, C_ACCENT, 0.25)
      .setOrigin(0, 0);
    this.container.add(sep1);
    this.container.add(sep2);

    const header = this.scene.add
      .rectangle(0, 0, WIDTH, HEADER_H, C_HEADER, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(1, C_ACCENT, 0.7);
    this.container.add(header);
    const accentLine = this.scene.add.rectangle(0, HEADER_H, WIDTH, 2, C_ACCENT, 0.9).setOrigin(0, 0);
    this.container.add(accentLine);

    // Tabs Intercambio (activo) / Bazar
    this.addTabButton(12, 'Intercambio', true, () => {});
    this.addTabButton(120, 'Bazar', false, () => this.callbacks.onSwitchToMarketplace?.());

    // Oro con moneda
    const coin = createUIIcon(this.scene, WIDTH - 170, HEADER_H / 2, 'gold', 16);
    if (coin) this.container.add(coin);
    this.goldText = this.scene.add
      .text(WIDTH - 158, HEADER_H / 2, formatGold(this.gold), font({ fontSize: '14px', color: GOLD_TEXT_COLOR, fontStyle: 'bold' }))
      .setOrigin(0, 0.5);
    this.container.add(this.goldText);

    const closeBtn = this.scene.add
      .text(WIDTH - 22, HEADER_H / 2, '✕', font({ fontSize: '18px', color: '#e0b0b0' }))
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    closeBtn.on('pointerover', () => closeBtn.setColor('#ff8080'));
    closeBtn.on('pointerout', () => closeBtn.setColor('#e0b0b0'));
    closeBtn.on('pointerdown', () => this.close());
    this.container.add(closeBtn);

    const headerHit = this.scene.add
      .rectangle(0, 0, WIDTH - 40, HEADER_H, 0xffffff, 0.001)
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: true });
    headerHit.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.dragging = true;
      this.dragOffsetX = pointer.x - this.x;
      this.dragOffsetY = pointer.y - this.y;
    });
    this.container.add(headerHit);
  }

  private addTabButton(x: number, label: string, active: boolean, onClick: () => void): void {
    const w = 100;
    const rect = this.scene.add
      .rectangle(x, 6, w, HEADER_H - 10, active ? 0x3a2e14 : 0x201a0d, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(1, active ? C_ACCENT : 0x4a3c1c, 1)
      .setInteractive({ useHandCursor: true });
    rect.on('pointerdown', onClick);
    this.container.add(rect);
    this.container.add(
      this.scene.add
        .text(x + w / 2, HEADER_H / 2, label, font({ fontSize: '13px', color: active ? '#f4dc92' : '#9a8e60', fontStyle: active ? 'bold' : 'normal' }))
        .setOrigin(0.5)
    );
  }

  // ===========================================================================
  // Render dinámico
  // ===========================================================================

  private clearDynamic(): void {
    for (const o of this.dynamicObjects) o.destroy();
    this.dynamicObjects = [];
  }

  private addDyn<T extends Phaser.GameObjects.GameObject>(obj: T): T {
    this.container.add(obj);
    this.dynamicObjects.push(obj);
    return obj;
  }

  private render(): void {
    this.clearDynamic();
    this.goldText.setText(formatGold(this.gold));
    if (this.alertUnseen > 0) {
      const bell = this.addDyn(
        this.scene.add
          .text(WIDTH - 230, HEADER_H / 2, `🔔 ${this.alertUnseen}`, font({ fontSize: '13px', color: '#ffd060', fontStyle: 'bold' }))
          .setOrigin(0, 0.5)
          .setInteractive({ useHandCursor: true })
      );
      bell.on('pointerdown', () => this.callbacks.onAlertsSeen());
    }
    this.renderCatalog();
    this.renderBook();
    this.renderActionColumn();
  }

  private txt(x: number, y: number, s: string, style: Phaser.Types.GameObjects.Text.TextStyle): Phaser.GameObjects.Text {
    return this.addDyn(this.scene.add.text(x, y, s, font(style)).setOrigin(0, 0));
  }

  private itemIcon(x: number, y: number, iconName: string, size: number): void {
    const key = `item_icon_${iconName}`;
    const tex = this.scene.textures.exists(key) ? key : 'item_icon_noicon';
    if (!this.scene.textures.exists(tex)) return;
    this.addDyn(this.scene.add.image(x, y, tex).setOrigin(0.5).setDisplaySize(size, size));
  }

  private coin(x: number, y: number, size = 13): void {
    const c = createUIIcon(this.scene, x, y, 'gold', size);
    if (c) this.addDyn(c);
  }

  private renderCatalog(): void {
    const x0 = 12;
    let y = HEADER_H + 12;
    this.txt(x0, y, 'FUNGIBLES', { fontSize: '11px', color: T_LABEL, fontStyle: 'bold' });
    y += 20;
    for (const entry of this.catalog) {
      const selected = entry.itemId === this.selectedItemId;
      const rowH = 42;
      const card = this.addDyn(
        this.scene.add
          .rectangle(x0, y, COL_CATALOG_W - 22, rowH, selected ? C_CARD_SEL : C_CARD, 1)
          .setOrigin(0, 0)
          .setStrokeStyle(1, selected ? C_ACCENT : 0x4a3c1c, 1)
          .setInteractive({ useHandCursor: true })
      );
      card.on('pointerdown', () => this.selectItem(entry.itemId));
      if (selected) {
        // Barra de acento a la izquierda.
        this.addDyn(this.scene.add.rectangle(x0, y, 3, rowH, C_ACCENT, 1).setOrigin(0, 0));
      }
      this.itemIcon(x0 + 23, y + rowH / 2, entry.itemIcon, 30);
      this.txt(x0 + 44, y + 6, entry.itemName, { fontSize: '12px', color: T_BODY, fontStyle: 'bold' });
      // Último precio + moneda
      this.coin(x0 + 48, y + 27, 11);
      this.txt(x0 + 56, y + 21, `${fmt(entry.lastPrice)}`, { fontSize: '12px', color: GOLD_TEXT_COLOR });
      const spread =
        entry.bestBid !== null && entry.bestAsk !== null ? `${entry.bestBid}/${entry.bestAsk}` : '—';
      this.txt(x0 + COL_CATALOG_W - 90, y + 21, `c/v ${spread}`, { fontSize: '10px', color: T_DIM });
      y += rowH + 6;
    }
  }

  private renderBook(): void {
    const x0 = COL_CATALOG_W + 14;
    const colW = COL_BOOK_W - 26;
    let y = HEADER_H + 12;
    const entry = this.catalog.find((e) => e.itemId === this.selectedItemId);

    // Header del item: icono + nombre + último precio grande.
    if (entry) this.itemIcon(x0 + 16, y + 16, entry.itemIcon, 32);
    this.txt(x0 + 38, y + 2, entry?.itemName ?? 'Seleccioná un item', { fontSize: '15px', color: '#f4dc92', fontStyle: 'bold' });
    this.coin(x0 + 42, y + 24, 13);
    this.txt(x0 + 52, y + 17, `${fmt(this.book.lastPrice)}`, { fontSize: '15px', color: GOLD_TEXT_COLOR, fontStyle: 'bold' });
    const bestBid = this.book.bids[0]?.price ?? null;
    const bestAsk = this.book.asks[0]?.price ?? null;
    const spread = bestBid !== null && bestAsk !== null ? bestAsk - bestBid : null;
    this.txt(x0 + colW - 96, y + 17, spread !== null ? `spread ${spread}` : 'spread —', { fontSize: '11px', color: T_DIM });
    y += 42;

    // Gráfico de velas + barras de volumen, con toggle 1h/1d.
    const candles = this.chartBucket === '1h' ? this.history : this.historyDay;
    this.txt(x0, y, this.chartBucket === '1h' ? 'PRECIO (24h)' : 'PRECIO (30d)', { fontSize: '10px', color: T_LABEL, fontStyle: 'bold' });
    // Toggle 1h | 1d
    const mkTog = (tx: number, label: string, bucket: '1h' | '1d'): void => {
      const active = this.chartBucket === bucket;
      const t = this.addDyn(
        this.scene.add
          .text(tx, y, label, font({ fontSize: '10px', color: active ? '#ffd060' : '#7a6e44', fontStyle: active ? 'bold' : 'normal' }))
          .setOrigin(0, 0)
          .setInteractive({ useHandCursor: true })
      );
      t.on('pointerdown', () => {
        if (this.chartBucket !== bucket) {
          this.chartBucket = bucket;
          this.render();
        }
      });
    };
    mkTog(x0 + colW - 48, '1h', '1h');
    mkTog(x0 + colW - 24, '1d', '1d');
    const totVol = candles.reduce((s, c) => s + c.volume, 0);
    this.txt(x0 + colW - 110, y, `vol ${totVol}`, { fontSize: '10px', color: T_DIM });
    y += 14;
    this.renderChart(x0, y, colW, 78, candles);
    y += 86;

    // Libro de profundidad
    const allQty = [...this.book.asks, ...this.book.bids].map((l) => l.quantity);
    const maxQty = Math.max(1, ...allQty);
    const barMaxW = colW - 8;

    this.txt(x0, y, 'VENTAS', { fontSize: '10px', color: '#d98a8a', fontStyle: 'bold' });
    y += 15;
    const asksShown = this.book.asks.slice(0, 4).reverse();
    if (asksShown.length === 0) {
      this.txt(x0, y, '— sin ventas —', { fontSize: '11px', color: '#6a6044' });
      y += 18;
    }
    for (const lvl of asksShown) {
      this.depthRow(x0, y, barMaxW, lvl, maxQty, false);
      y += 18;
    }

    // Mid (último precio).
    this.addDyn(this.scene.add.rectangle(x0, y + 2, colW, 1, C_ACCENT, 0.4).setOrigin(0, 0));
    this.coin(x0 + 6, y + 9, 10);
    this.txt(x0 + 13, y + 4, `${fmt(this.book.lastPrice)}`, { fontSize: '11px', color: GOLD_TEXT_COLOR });
    y += 16;

    this.txt(x0, y, 'COMPRAS', { fontSize: '10px', color: '#8ad98a', fontStyle: 'bold' });
    y += 15;
    const bidsShown = this.book.bids.slice(0, 4);
    if (bidsShown.length === 0) {
      this.txt(x0, y, '— sin compras —', { fontSize: '11px', color: '#6a6044' });
      y += 18;
    }
    for (const lvl of bidsShown) {
      this.depthRow(x0, y, barMaxW, lvl, maxQty, true);
      y += 18;
    }
  }

  /** Dibuja un gráfico de velas OHLC (verde sube / rojo baja) + barras de volumen. */
  private renderChart(x: number, y: number, w: number, h: number, candles: PriceCandle[]): void {
    this.addDyn(this.scene.add.rectangle(x, y, w, h, 0x100d08, 1).setOrigin(0, 0).setStrokeStyle(1, 0x3a2f16, 1));
    if (candles.length === 0) {
      this.addDyn(
        this.scene.add.text(x + w / 2, y + h / 2, 'sin historial todavía', font({ fontSize: '11px', color: '#6a6044' })).setOrigin(0.5)
      );
      return;
    }
    let min = Infinity;
    let max = -Infinity;
    let maxVol = 1;
    for (const c of candles) {
      min = Math.min(min, c.low);
      max = Math.max(max, c.high);
      maxVol = Math.max(maxVol, c.volume);
    }
    const range = Math.max(1e-6, max - min);
    const n = candles.length;
    const slot = w / Math.max(n, 1);
    const bodyW = Math.max(2, Math.min(slot * 0.6, 14));
    const volH = 16;
    const chartH = h - volH - 2;
    const priceY = (p: number): number => y + 2 + (chartH - 4) - ((p - min) / range) * (chartH - 4);

    const g = this.addDyn(this.scene.add.graphics());
    candles.forEach((c, i) => {
      const cx = x + slot * (i + 0.5);
      const up = c.close >= c.open;
      const color = up ? 0x46c46a : 0xe06a6a;
      // Mecha (high-low)
      g.lineStyle(1, color, 1);
      g.beginPath();
      g.moveTo(cx, priceY(c.high));
      g.lineTo(cx, priceY(c.low));
      g.strokePath();
      // Cuerpo (open-close)
      const yo = priceY(c.open);
      const yc = priceY(c.close);
      const top = Math.min(yo, yc);
      const bh = Math.max(1, Math.abs(yc - yo));
      g.fillStyle(color, 1);
      g.fillRect(cx - bodyW / 2, top, bodyW, bh);
      // Volumen
      const vh = (c.volume / maxVol) * (volH - 2);
      g.fillStyle(up ? 0x2a5a38 : 0x5a2a2a, 0.95);
      g.fillRect(cx - bodyW / 2, y + h - vh - 1, bodyW, vh);
    });
    // Labels de rango
    this.addDyn(this.scene.add.text(x + 3, y + 1, String(max), font({ fontSize: '9px', color: '#8a7e54' })).setOrigin(0, 0));
    this.addDyn(this.scene.add.text(x + 3, y + chartH - 11, String(min), font({ fontSize: '9px', color: '#8a7e54' })).setOrigin(0, 0));
  }

  /** Una fila del libro con barra de profundidad proporcional a la cantidad. */
  private depthRow(x: number, y: number, barMaxW: number, lvl: OrderBookLevel, maxQty: number, isBid: boolean): void {
    const w = Math.max(3, Math.round((lvl.quantity / maxQty) * barMaxW));
    const barColor = isBid ? C_GREEN_BAR : C_RED_BAR;
    // Las barras de bid crecen desde la izquierda; las de ask también (simple y legible).
    this.addDyn(this.scene.add.rectangle(x, y, w, 15, barColor, 0.85).setOrigin(0, 0));
    const priceColor = isBid ? '#a6e6a6' : '#e6a6a6';
    this.addDyn(this.scene.add.text(x + 5, y + 1, String(lvl.price), font({ fontSize: '11px', color: priceColor })).setOrigin(0, 0));
    this.addDyn(
      this.scene.add
        .text(x + barMaxW, y + 1, String(lvl.quantity), font({ fontSize: '11px', color: '#cdbf8c' }))
        .setOrigin(1, 0)
    );
  }

  private renderActionColumn(): void {
    const x0 = COL_CATALOG_W + COL_BOOK_W + 14;
    const colW = WIDTH - x0 - 14;
    let y = HEADER_H + 12;

    if (!this.selectedItemId) return;
    const owned = this.ownedByItem.get(this.selectedItemId) ?? 0;

    this.txt(x0, y, 'NUEVA ORDEN', { fontSize: '11px', color: T_LABEL, fontStyle: 'bold' });
    y += 18;
    this.makeToggle(x0, y, colW);
    y += 34;

    this.txt(x0, y, 'Precio (oro/u)', { fontSize: '11px', color: '#bfae78' });
    y += 16;
    this.makeStepper(x0, y, colW, this.price, (delta) => {
      this.price = Math.max(1, this.price + delta);
      this.render();
    });
    y += 30;

    this.txt(x0, y, this.side === MarketSide.SELL ? `Cantidad (tenés ${owned})` : 'Cantidad', { fontSize: '11px', color: '#bfae78' });
    y += 16;
    this.makeStepper(x0, y, colW, this.quantity, (delta) => {
      this.quantity = Math.max(1, this.quantity + delta);
      this.render();
    });
    y += 30;

    // Total con moneda
    const total = this.price * this.quantity;
    this.txt(x0, y, 'Total', { fontSize: '11px', color: T_DIM });
    this.coin(x0 + 44, y + 7, 12);
    this.txt(x0 + 52, y, `${formatGold(total)}`, { fontSize: '13px', color: GOLD_TEXT_COLOR, fontStyle: 'bold' });
    this.txt(x0 + colW - 40, y + 1, '+1 fee', { fontSize: '10px', color: T_DIM });
    y += 22;

    const isBuy = this.side === MarketSide.BUY;
    this.makeButton(x0, y, colW, 30, isBuy ? 'CREAR COMPRA' : 'CREAR VENTA', isBuy ? 0x2e6a38 : 0x6a2e2e, () => {
      if (this.selectedItemId) this.callbacks.onCreateOrder(this.selectedItemId, this.side, this.price, this.quantity);
    });
    y += 44;

    // Mis órdenes
    this.txt(x0, y, 'MIS ÓRDENES', { fontSize: '11px', color: T_LABEL, fontStyle: 'bold' });
    y += 16;
    if (this.myOrders.length === 0) {
      this.txt(x0, y, '— ninguna —', { fontSize: '11px', color: '#6a6044' });
      y += 16;
    }
    for (const o of this.myOrders) {
      const buy = o.side === MarketSide.BUY;
      const remaining = o.qtyTotal - o.qtyFilled;
      this.addDyn(this.scene.add.rectangle(x0, y, 14, 14, buy ? C_GREEN : C_RED, 1).setOrigin(0, 0));
      this.txt(x0 + 4, y + 1, buy ? 'C' : 'V', { fontSize: '10px', color: '#101010', fontStyle: 'bold' });
      this.txt(x0 + 20, y + 1, `${remaining}/${o.qtyTotal} @${o.price}`, { fontSize: '11px', color: '#d8cda0' });
      const cancel = this.txt(x0 + colW - 54, y + 1, 'Cancelar', { fontSize: '11px', color: '#e09090' });
      cancel.setInteractive({ useHandCursor: true });
      cancel.on('pointerover', () => cancel.setColor('#ff8080'));
      cancel.on('pointerout', () => cancel.setColor('#e09090'));
      cancel.on('pointerdown', () => this.callbacks.onCancelOrder(o.id));
      y += 19;
    }
    y += 12;

    // Cajón de retiro
    const claimCount = this.claims.length;
    this.txt(x0, y, `CAJÓN DE RETIRO (${claimCount})`, { fontSize: '11px', color: claimCount > 0 ? '#e6c860' : T_LABEL, fontStyle: 'bold' });
    y += 16;
    for (const c of this.claims.slice(0, 4)) {
      if (c.kind === 'gold') {
        this.coin(x0 + 6, y + 7, 11);
        this.txt(x0 + 14, y, `${formatGold(c.goldAmount)} oro`, { fontSize: '11px', color: GOLD_TEXT_COLOR });
      } else {
        this.itemIcon(x0 + 8, y + 7, c.itemIcon ?? '', 14);
        this.txt(x0 + 18, y, `${c.quantity}× ${c.itemName ?? c.itemId}`, { fontSize: '11px', color: '#cdbf8c' });
      }
      y += 16;
    }
    if (claimCount > 4) {
      this.txt(x0, y, `… +${claimCount - 4} más`, { fontSize: '10px', color: T_DIM });
      y += 16;
    }
    if (claimCount > 0) {
      y += 4;
      this.makeButton(x0, y, colW, 26, 'RECLAMAR TODO', 0x3a3318, () => this.callbacks.onClaimAll());
      y += 32;
    } else {
      y += 6;
    }

    // Vigilar precio (watchlist)
    if (this.watchPrice <= 1) this.watchPrice = this.book.lastPrice ?? this.book.asks[0]?.price ?? 1;
    this.txt(x0, y, 'VIGILAR PRECIO', { fontSize: '11px', color: T_LABEL, fontStyle: 'bold' });
    y += 16;
    // Toggle dirección ≤ / ≥
    const dirBtn = this.addDyn(
      this.scene.add
        .rectangle(x0, y, 30, 22, 0x241d10, 1)
        .setOrigin(0, 0)
        .setStrokeStyle(1, C_ACCENT, 1)
        .setInteractive({ useHandCursor: true })
    );
    dirBtn.on('pointerdown', () => {
      this.watchDir = this.watchDir === 'below' ? 'above' : 'below';
      this.render();
    });
    this.addDyn(this.scene.add.text(x0 + 15, y + 11, this.watchDir === 'below' ? '≤' : '≥', font({ fontSize: '15px', color: '#ffd060' })).setOrigin(0.5));
    // Precio objetivo (stepper)
    this.makeMiniStepper(x0 + 36, y, '−', () => {
      this.watchPrice = Math.max(1, this.watchPrice - 1);
      this.render();
    });
    this.addDyn(this.scene.add.rectangle(x0 + 62, y, 56, 22, 0x12100a, 1).setOrigin(0, 0).setStrokeStyle(1, 0x4a3c1c, 1));
    this.addDyn(this.scene.add.text(x0 + 90, y + 11, String(this.watchPrice), font({ fontSize: '13px', color: '#ffffff' })).setOrigin(0.5));
    this.makeMiniStepper(x0 + 120, y, '+', () => {
      this.watchPrice += 1;
      this.render();
    });
    this.makeButton(x0 + 150, y, colW - 150, 22, 'VIGILAR', 0x3a3318, () => {
      if (this.selectedItemId) this.callbacks.onAddWatch(this.selectedItemId, this.watchDir, this.watchPrice);
    });
    y += 28;
    // Vigilancias activas del item seleccionado
    for (const w of this.watches.filter((w) => w.itemId === this.selectedItemId)) {
      this.txt(x0, y + 1, `${w.direction === 'below' ? '≤' : '≥'} ${w.targetPrice} oro`, { fontSize: '11px', color: '#d8cda0' });
      const del = this.txt(x0 + colW - 16, y + 1, '✕', { fontSize: '12px', color: '#e09090' });
      del.setInteractive({ useHandCursor: true });
      del.on('pointerdown', () => this.callbacks.onRemoveWatch(w.id));
      y += 16;
    }
  }

  private makeMiniStepper(x: number, y: number, label: string, onClick: () => void): void {
    const rect = this.addDyn(
      this.scene.add.rectangle(x, y, 24, 22, 0x2a2210, 1).setOrigin(0, 0).setStrokeStyle(1, 0x4a3c1c, 1).setInteractive({ useHandCursor: true })
    );
    rect.on('pointerdown', onClick);
    this.addDyn(this.scene.add.text(x + 12, y + 11, label, font({ fontSize: '14px', color: '#d8c890' })).setOrigin(0.5));
  }

  // ===========================================================================
  // Helpers de input
  // ===========================================================================

  private selectItem(itemId: string): void {
    if (this.selectedItemId === itemId) return;
    this.selectedItemId = itemId;
    this.quantity = 1;
    this.book = { bids: [], asks: [], lastPrice: null };
    this.myOrders = [];
    this.recentTrades = [];
    this.history = [];
    this.historyDay = [];
    this.render();
    this.callbacks.onViewItem(itemId);
  }

  private defaultPriceForSide(side: MarketSide): number {
    if (side === MarketSide.BUY && this.book.asks.length > 0) return this.book.asks[0].price;
    if (side === MarketSide.SELL && this.book.bids.length > 0) return this.book.bids[0].price;
    return this.book.lastPrice ?? 1;
  }

  private makeToggle(x: number, y: number, w: number): void {
    const halfW = (w - 6) / 2;
    const mk = (bx: number, label: string, side: MarketSide, activeColor: number): void => {
      const active = this.side === side;
      const rect = this.addDyn(
        this.scene.add
          .rectangle(bx, y, halfW, 28, active ? activeColor : 0x241d10, 1)
          .setOrigin(0, 0)
          .setStrokeStyle(active ? 2 : 1, active ? C_ACCENT : 0x4a3c1c, 1)
          .setInteractive({ useHandCursor: true })
      );
      rect.on('pointerdown', () => {
        if (this.side !== side) {
          this.side = side;
          this.price = this.defaultPriceForSide(side);
          this.render();
        }
      });
      this.addDyn(
        this.scene.add
          .text(bx + halfW / 2, y + 14, label, font({ fontSize: '13px', color: active ? '#ffffff' : '#9a8e60', fontStyle: active ? 'bold' : 'normal' }))
          .setOrigin(0.5)
      );
    };
    mk(x, 'COMPRAR', MarketSide.BUY, 0x2e6a38);
    mk(x + halfW + 6, 'VENDER', MarketSide.SELL, 0x6a2e2e);
  }

  private makeStepper(x: number, y: number, w: number, value: number, onDelta: (delta: number) => void): void {
    const btnW = 30;
    const mk = (bx: number, label: string, delta: number): void => {
      const rect = this.addDyn(
        this.scene.add
          .rectangle(bx, y, btnW, 26, 0x2a2210, 1)
          .setOrigin(0, 0)
          .setStrokeStyle(1, 0x4a3c1c, 1)
          .setInteractive({ useHandCursor: true })
      );
      rect.on('pointerover', () => rect.setFillStyle(0x3a3018, 1));
      rect.on('pointerout', () => rect.setFillStyle(0x2a2210, 1));
      rect.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
        const mult = pointer.event && (pointer.event as PointerEvent).shiftKey ? 10 : 1;
        onDelta(delta * mult);
      });
      this.addDyn(this.scene.add.text(bx + btnW / 2, y + 13, label, font({ fontSize: '16px', color: '#d8c890' })).setOrigin(0.5));
    };
    mk(x, '−', -1);
    this.addDyn(
      this.scene.add.rectangle(x + btnW + 4, y, w - 2 * btnW - 8, 26, 0x12100a, 1).setOrigin(0, 0).setStrokeStyle(1, 0x4a3c1c, 1)
    );
    this.addDyn(this.scene.add.text(x + w / 2, y + 13, String(value), font({ fontSize: '15px', color: '#ffffff', fontStyle: 'bold' })).setOrigin(0.5));
    mk(x + w - btnW, '+', 1);
  }

  private makeButton(x: number, y: number, w: number, h: number, label: string, color: number, onClick: () => void): void {
    const rect = this.addDyn(
      this.scene.add
        .rectangle(x, y, w, h, color, 1)
        .setOrigin(0, 0)
        .setStrokeStyle(1, C_ACCENT, 1)
        .setInteractive({ useHandCursor: true })
    );
    rect.on('pointerover', () => rect.setFillStyle(brighten(color), 1));
    rect.on('pointerout', () => rect.setFillStyle(color, 1));
    rect.on('pointerdown', onClick);
    this.addDyn(this.scene.add.text(x + w / 2, y + h / 2, label, font({ fontSize: '13px', color: '#f4ecc8', fontStyle: 'bold' })).setOrigin(0.5));
  }
}

// ===========================================================================
// Utilidades
// ===========================================================================

function formatGold(n: number): string {
  return Math.floor(n).toLocaleString('es-AR');
}

function fmt(n: number | null): string {
  return n === null || n === undefined ? '—' : String(n);
}

function brighten(color: number): number {
  const r = Math.min(255, ((color >> 16) & 0xff) + 24);
  const g = Math.min(255, ((color >> 8) & 0xff) + 24);
  const b = Math.min(255, (color & 0xff) + 24);
  return (r << 16) | (g << 8) | b;
}
